import { MinimalStepperProps } from '@components';
import { showToast } from '@design-system';
import { getUser } from '@features/auth';
import { ApiFile } from '@hooks/useFlowTs';
import { User } from '@sentry/react';
import {
    Apartment,
    CreateQuitusPayload,
    FollowUpValues,
    QuitusType,
    useCreateQuitusMutation,
    useGetQuitusTypesMutation,
    useGetUsersMutation,
    useUploadFileMutation,
    Worksite
} from '@services';
import { transformSignatureToFile } from '@utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

export const useNewQuitusPageVM = () => {
    const [currentStep, setCurrentStep] = useState<Step>(1);
    const [steps] = useState<MinimalStepperProps['steps']>([
        { label: 'Informations client', status: 'inProgress' },
        { label: 'Informations', status: 'pending' },
        { label: 'Travaux', status: 'pending' },
        { label: 'Photos', status: 'pending' },
        { label: 'Commentaire', status: 'pending' },
        { label: 'Suite et signature', status: 'pending' }
    ]);
    const [operators, setOperators] = useState<User[]>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<{ id: string; label: string }[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [apartmentsOptions, setApartmentsOptions] = useState<{ id: string; label: string }[]>([]);
    const [buildingsOptions, setBuildingsOptions] = useState<{ id: string; label: string }[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [selectedApartmentId, setSelectedApartmentId] = useState<string>('');
    const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
    const [quitusTypesOptions, setQuitusTypesOptions] = useState<{ id: string; label: string }[]>([]);
    const [completedWorksOptions] = useState<{ id: string; label: string }[]>([]);
    const [completedWorks, setCompletedWorks] = useState<string[]>([]);
    const [files, setFiles] = useState<ApiFile[]>([]);
    const signatureCanvasRef = useRef<SignatureCanvas>(null);
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const [signature, setSignature] = useState<string | null>(null);

    const user = useSelector(getUser);

    const [quitus, setQuitus] = useState<Omit<CreateQuitusPayload, 'type'> & { type: QuitusType }>({
        call_date: new Date().toISOString().split('T')[0],
        call_by: user?.first_name + ' ' + user?.last_name,
        client: '',
        address: '',
        phone_number: '',
        cellphone_number: '',
        type: { id: '', label: '' },
        other: '',
        start_time: '',
        end_time: '',
        observations: '',
        request_nature: '',
        completed_works: '',
        worksite_id: 0,
        apartment_id: 0,
        building_id: 0,
        apartment_other: '',
        follow_up: [],
        images: [],
        client_signature: ''
    });

    const [getUsers, { data: users }] = useGetUsersMutation();
    const [getQuitusTypes, { data: quitusTypes }] = useGetQuitusTypesMutation();
    const [uploadFile, { isError: isFileUploadError, error: fileUploadError, data: fileUploadData }] = useUploadFileMutation();
    const [createQuitus, { isError: isCreateQuitusError, error: createQuitusError, isSuccess: isCreateQuitusSuccess }] =
        useCreateQuitusMutation();

    const navigate = useNavigate();

    useEffect(() => {
        getUsers();
        getQuitusTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (users) {
            setOperators(users.operators);
            const operatorsOpts = users.operators.map((op) => ({
                id: op.id_operator,
                label: op.name
            }));
            setOperatorsOptions(operatorsOpts);
        }
    }, [users]);

    useEffect(() => {
        if (quitusTypes) {
            setQuitusTypesOptions(quitusTypes);
        }
    }, [quitusTypes]);

    const handleNextStep = () => {
        if (currentStep === 6) {
            handleCreateQuitus();
            return;
        }
        setCurrentStep((prev) => (prev < 6 ? ((prev + 1) as Step) : prev));
    };

    const handlePrevStep = () => {
        setCurrentStep((prev) => (prev > 1 ? ((prev - 1) as Step) : prev));
    };

    const handleChange = (field: keyof CreateQuitusPayload, value: string | QuitusType | FollowUpValues[]) => {
        setQuitus((prev) => ({ ...prev, [field]: value }));
    };

    const handleOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');

        const selectedOperator = operators.find((op) => op.id_operator === operatorId);
        if (selectedOperator) {
            const worksites = selectedOperator.worksites.map((worksite: Worksite) => ({
                id: worksite.id,
                label: worksite.name
            }));
            setWorksitesOptions(worksites);
        } else {
            setWorksitesOptions([]);
        }
    };

    const handleWorksiteChange = (worksiteId: string) => {
        setSelectedWorksiteId(worksiteId);
        setSelectedApartmentId('');
        setQuitus((prev) => ({ ...prev, worksite_id: Number(worksiteId), apartment_id: 0 }));

        const selectedOperator = operators.find((op) => op.id_operator === selectedOperatorId);
        if (!selectedOperator) return;

        const selectedWorksite = selectedOperator.worksites.find((ws: Worksite) => ws.id === worksiteId);
        if (selectedWorksite) {
            setApartmentsOptions(
                selectedWorksite.apartments.map((apartment: Apartment) => ({
                    id: apartment.id,
                    label: apartment.title
                }))
            );
            if (selectedWorksite.buildings && selectedWorksite.buildings.length > 0) {
                setBuildingsOptions(
                    selectedWorksite.buildings.map((building: User['buildings']) => ({
                        id: building.id,
                        label: building.name
                    }))
                );
            } else {
                setBuildingsOptions([]);
            }
        }
    };

    const handleApartmentChange = (apartmentId: string) => {
        setSelectedApartmentId(apartmentId);
        setQuitus((prev) => ({ ...prev, apartment_id: Number(apartmentId) }));
    };

    const handleBuildingChange = (buildingId: string) => {
        setSelectedBuildingId(buildingId);
        setQuitus((prev) => ({ ...prev, building_id: Number(buildingId) }));
        // Si on sélectionne un bâtiment, on désélectionne l'appartement
        setSelectedApartmentId('');
    };

    const handleCompletedWorksChange = (completedWorksValues: string[]) => {
        setCompletedWorks(completedWorksValues);
        setQuitus((prev) => ({ ...prev, completed_works: completedWorksValues.join(', ') }));
        setTimeout(() => {
            const scrollContainer = document.querySelector('.rendered-page') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
            }
        }, 100);
    };

    const handleClearSignature = () => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
            setIsEmpty(true);
            setSignature('');
        }
    };

    const handleSaveSignature = () => {
        if (signatureCanvasRef.current) {
            const isEmpty = signatureCanvasRef.current.isEmpty();
            if (isEmpty) {
                showToast({
                    message: 'Veuillez dessiner votre signature avant de continuer',
                    type: 'error'
                });
                return;
            }

            const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
            const file = transformSignatureToFile(dataUrl);

            const formData = new FormData();
            formData.append('file', file);

            uploadFile(formData);
        }
    };

    const handleCreateQuitus = () => {
        handleSaveSignature();
    };

    const computedStepperSteps = useMemo((): MinimalStepperProps['steps'] => {
        return steps.map((item, index) => {
            const computedStatus = index < currentStep ? 'completed' : index === currentStep ? 'inProgress' : 'pending';
            return {
                label: item.label,
                status: computedStatus
            };
        });
    }, [steps, currentStep]);

    const canGoNext = useMemo(() => {
        const {
            client,
            address,
            phone_number,
            cellphone_number,
            type,
            start_time,
            end_time,
            call_date,
            request_nature,
            completed_works,
            worksite_id,
            apartment_id,
            building_id,
            apartment_other
        } = quitus;

        switch (currentStep) {
            case 1:
                return client.length > 0 && address.length > 0 && phone_number.length >= 10 && cellphone_number.length >= 10;
            case 2:
                return (
                    call_date.length > 0 &&
                    start_time.length > 0 &&
                    end_time.length > 0 &&
                    worksite_id > 0 &&
                    (apartment_id > 0 || apartment_other.length > 0 || building_id > 0)
                );
            case 3:
                return type.id !== '' && request_nature.length > 0 && completed_works.length > 0;
            case 4:
                return true;
            case 5:
                return true;
            case 6:
                return !isEmpty;
            default:
                return true;
        }
    }, [currentStep, quitus, isEmpty]);

    const canGoPrev = useMemo(() => {
        return currentStep > 1;
    }, [currentStep]);

    useEffect(() => {
        if (files.length > 0) {
            setQuitus((prev) => ({ ...prev, images: files.map((file: ApiFile) => file.url) }));
        } else {
            setQuitus((prev) => ({ ...prev, images: [] }));
        }
    }, [files]);

    useEffect(() => {
        if (isFileUploadError && fileUploadError) {
            console.error('fileUploadError:', fileUploadError);
        }
        if (fileUploadData) {
            setSignature(fileUploadData.original);
        }
    }, [fileUploadData, isFileUploadError, fileUploadError, setSignature]);

    useEffect(() => {
        const quitusTypeId = quitusTypesOptions.find((type) => type.id === quitus.type.id)?.id;
        if (!signature || isCreateQuitusError || createQuitusError || !quitusTypeId) return;
        createQuitus({
            ...quitus,
            worksite_id: Number(quitus.worksite_id),
            apartment_id: Number(quitus.apartment_id),
            client_signature: signature,
            type: Number(quitusTypeId),
            start_time: quitus.start_time.split('T')[0],
            end_time: quitus.end_time.split('T')[0]
        });
    }, [signature, createQuitus, quitus, isCreateQuitusError, createQuitusError, quitusTypesOptions]);

    useEffect(() => {
        if (isCreateQuitusError) {
            return;
        }
        if (isCreateQuitusSuccess) {
            showToast({
                message: 'Quitus créé avec succès',
                type: 'success'
            });
            navigate('/mes-chantiers/quitus');
        }
    }, [isCreateQuitusError, createQuitusError, isCreateQuitusSuccess, navigate]);

    return {
        currentStep,
        steps,
        operatorsOptions,
        worksitesOptions,
        apartmentsOptions,
        quitusTypesOptions,
        buildingsOptions,
        completedWorksOptions,
        completedWorks,
        files,
        setFiles,
        selectedOperatorId,
        selectedWorksiteId,
        selectedApartmentId,
        selectedBuildingId,
        handleNextStep,
        handlePrevStep,
        computedStepperSteps,
        handleChange,
        handleOperatorChange,
        handleWorksiteChange,
        handleApartmentChange,
        handleBuildingChange,
        handleCompletedWorksChange,
        signatureCanvasRef,
        isEmpty,
        setIsEmpty,
        handleClearSignature,
        quitus,
        canGoNext,
        canGoPrev,
        signature,
        setSignature,
        handleCreateQuitus
    };
};
